import { EventName } from "game/events/eventsName";
import { GameEvent } from "game/events/gameEvent";
import { UIState, updateUIState, uiStore, showImagePreviewModal } from "ui";
import { StoryConfig } from "./types";
import { Store, TICKETS_NEXT_SHOW } from "game/store/gameStore";
import { GAEvent, GAEventNames } from "game/analytics/events";

export class StoriesHandler {
    private static instance: StoriesHandler;
    private stories: StoryConfig[] = [];

    constructor() {
        StoriesHandler.instance = this;
        document.addEventListener('ui-button-click', this.handleUIButtonClick.bind(this));
        GameEvent.add(EventName.GAME_READY, () => {
            setTimeout(() => this.init(), 200);
        });
    }

    public static getAllStories() {
        return this.instance.stories;
    }

    public static showStories(targetStory?: StoryConfig) {
        this.instance.onOpenStories();

        this.instance.updateUI({
            modals: {
                ...uiStore.getState().gameUI.modals,
                storiesModal: { ...uiStore.getState().gameUI.modals.storiesModal, forceSelectedStory: targetStory }
            }
        });
    }

    private handleUIButtonClick(event: Event) {
        const customEvent = event as CustomEvent;
        const { buttonKey, data } = customEvent.detail;

        switch (buttonKey) {
            case 'stories':
            case 'whats-new-btn-stories':
                this.onOpenStories();
                break;
            case 'close-stories':
                this.onHideStories();
                break;
            case 'watch-story-video-btn':
                this.handleWatchStoryVideo(data);
            break;
            case 'stories-use-tickets-btn':
                this.handleStoriesUseTickets();
            break;
            case 'win-popup-stories-btn':
                this.handleWinPopupStories();
            break;
        }
    }

    private handleWinPopupStories() {
        const lastStory = this.stories.find((story, index) => {
            const lastStoryProgress = parseInt(localStorage.getItem(`stories_story_${index + 1}_progress`) || '0');
            return story.totalImages !== lastStoryProgress;
        });

        GAEvent(GAEventNames.STORIES_WIN_POPUP_BUTTON, {
            type: Store.getGameTypeText(),
            difficulty: Store.getDifficultyText(),
        });

        this.onOpenStories();
        StoriesHandler.showStories(lastStory);
        dispatchEvent(new CustomEvent('newWinGame'));
    }

    private handleStoriesUseTickets() {
        const lastStory = this.stories.find((story, index) => {
            const lastStoryProgress = parseInt(localStorage.getItem(`stories_story_${index + 1}_progress`) || '0');
            return story.totalImages !== lastStoryProgress;
        });

        this.onOpenStories();
        StoriesHandler.showStories(lastStory && Store.wins > TICKETS_NEXT_SHOW ? lastStory : undefined);
    }

    private handleWatchStoryVideo(data?: { storyId?: string | number; storyNumber?: number }) {
        if (!data || !data.storyId) {
            console.warn('watch-story-video-btn: missing storyId in event data');
            return;
        }

        const storyId = data.storyId;
        const storyNumber = data.storyNumber ?? 0;

        uiStore.dispatch(showImagePreviewModal({
            id: Number(storyNumber),
            storyName: storyId as string,
            placement: 'congratulations',
            images: [{ src: `/assets/stories/${storyId}/images/video`, isUnlocked: true, alt: 'Story video' }],
            initialSlideIndex: 0,
        }));
    }

    private async init() {
        try {
            const fetchConfig = () => import(`./__data__/stories_config.json`);
            const storiesModule = await fetchConfig();
            this.stories = storiesModule.default;

            this.updateUI({
                modals: {
                    ...uiStore.getState().gameUI.modals,
                    storiesModal: { ...uiStore.getState().gameUI.modals.storiesModal, stories: this.stories }
                }
            });
        } catch {
            console.log('Stories config was not detected!');
        }
    }

    updateUI(updates: Partial<UIState>) {
        uiStore.dispatch(updateUIState(updates));
    }

    private onOpenStories() {
        GameEvent.emit(EventName.OPEN_SETTINGS);
        
        this.updateUI({
            modals: {
                ...uiStore.getState().gameUI.modals,
                storiesModal: { ...uiStore.getState().gameUI.modals.storiesModal, isOpen: true },
                designModal: { ...uiStore.getState().gameUI.modals.designModal, isOpen: false },
                settingsModal: { isOpen: false },
                missionsModal: { ...uiStore.getState().gameUI.modals.missionsModal, isOpen: false }
            },
            isExpanded: true
        });
    }

    private onHideStories() {
        GameEvent.emit(EventName.HIDE_SETTINGS);
        
        this.updateUI({
            modals: {
                ...uiStore.getState().gameUI.modals,
                storiesModal: { ...uiStore.getState().gameUI.modals.storiesModal, isOpen: false, forceSelectedStory: undefined },
            },
            isExpanded: false
        });
    }
}